import { isWorkItemStatusSelector } from "./assessment_work_items.dto";
import type {
    AssessmentWorkItemListFilters, AssessmentWorkItemStatusSelector,
    AssessmentWorkItemType
} from "./assessment_work_items.types";

export type HeaderWorkItemFilters = Omit<AssessmentWorkItemListFilters,
    "view" | "itemType" | "requirement"> & {
    view: "header";
    itemType: AssessmentWorkItemType;
    page: number;
    pageSize: number;
};

export type HeaderWorkItemFilterChanges = Partial<Pick<HeaderWorkItemFilters,
    "search" | "assignedToMe" | "assigneeUserIds" | "assessmentIds" |
    "statuses" | "itemType">>;

const DEFAULT_PAGE_SIZE = 25;
const hasOwn = (value: object, key: string): boolean =>
    Object.prototype.hasOwnProperty.call(value, key);

const requireItemType = (value: AssessmentWorkItemType): AssessmentWorkItemType => {
    if (value !== "all" && value !== "task" && value !== "evidence") {
        throw new Error("A valid header item type is required.");
    }
    return value;
};

const compatibleStatuses = (statuses: readonly AssessmentWorkItemStatusSelector[] | undefined,
    itemType: AssessmentWorkItemType): AssessmentWorkItemStatusSelector[] | undefined => {
    if (statuses === undefined) return undefined;
    if (!Array.isArray(statuses) || statuses.some((status) => !isWorkItemStatusSelector(status))) {
        throw new Error("Valid work-item statuses are required.");
    }
    const selected = statuses.filter((status) =>
        (itemType === "all" || status.startsWith(`${itemType}:`)));
    return selected.length ? [...new Set(selected)] : undefined;
};

export const initialHeaderWorkItemFilters = (): HeaderWorkItemFilters => ({
    view: "header",
    itemType: "all",
    assignedToMe: true,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE
});

// Clearing the header filters enters the existing all-in-scope view. The API
// still decides which tasks and evidence the current user may see.
export const allScopeHeaderWorkItemFilters = (): HeaderWorkItemFilters => ({
    ...initialHeaderWorkItemFilters(),
    assignedToMe: false
});

// Explicit field copies prevent task-only selectors, sort, or a footer view
// from reaching the combined header API through a spread of UI state.
export const changeHeaderWorkItemFilters = (current: HeaderWorkItemFilters,
    changes: HeaderWorkItemFilterChanges): HeaderWorkItemFilters => {
    const itemType = requireItemType(hasOwn(changes, "itemType")
        ? changes.itemType as AssessmentWorkItemType : current.itemType);
    const assignedToMe = hasOwn(changes, "assignedToMe")
        ? changes.assignedToMe : current.assignedToMe;
    if (typeof assignedToMe !== "boolean") {
        throw new Error("A valid assigned-to-me filter is required.");
    }
    const next: HeaderWorkItemFilters = {
        view: "header",
        itemType,
        page: 1,
        pageSize: current.pageSize,
        assignedToMe
    };
    const search = hasOwn(changes, "search") ? changes.search : current.search;
    if (search !== undefined) next.search = search;
    const assigneeUserIds = hasOwn(changes, "assigneeUserIds")
        ? changes.assigneeUserIds : current.assigneeUserIds;
    if (assigneeUserIds !== undefined) next.assigneeUserIds = [...assigneeUserIds];
    const assessmentIds = hasOwn(changes, "assessmentIds")
        ? changes.assessmentIds : current.assessmentIds;
    if (assessmentIds !== undefined) next.assessmentIds = [...assessmentIds];
    const statuses = hasOwn(changes, "statuses") ? changes.statuses : current.statuses;
    const compatible = compatibleStatuses(statuses, itemType);
    if (compatible) next.statuses = compatible;
    return next;
};
